import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { sentBetween, type DayRange } from '@/lib/automation-replies';
import { readDeviationParams, writeDeviationParams, type DeviationReviewedFilter, type DeviationUrlPatch } from '@/lib/deviation-url';
import type { SettingsColumnHead } from '@/modules/settings';
import type { DeviationFilter, DeviationSentVia, DeviationSort } from '@/services/deviation.service';
import { EditRateSortHeader } from './deviation-sort-header';

/** The Edit % presets in the Filters popover: a minimum, no maximum. */
export const EDIT_RATE_PRESETS = [30, 50, 70] as const;

const SEARCH_PAUSE_MS = 300;

/** The Deviations log's column titles, rendered in the page's sticky header; Edit % sorts. */
export function deviationLogColumns(sort: DeviationSort, onSort: (next: DeviationSort) => void): SettingsColumnHead[] {
	return [
		{ id: 'time', header: 'Time', className: 'w-[165px]' },
		{ id: 'ticket', header: 'Ticket ID', className: 'w-[120px]' },
		{ id: 'intent', header: 'Intent', className: 'min-w-[150px] flex-1' },
		{ id: 'sentBy', header: 'Sent by', className: 'w-[200px]' },
		{ id: 'aiScore', header: <span title='The AI check score against the brand threshold'>AI score</span>, className: 'w-[110px]' },
		{ id: 'writingIssues', header: 'Writing issues', className: 'w-[180px]' },
		{ id: 'editRate', header: <EditRateSortHeader sort={sort} onSort={onSort} />, className: 'w-[150px]' }
	];
}

/**
 * The search box, filters and order of the Deviations log, kept in the URL so a filtered view can
 * be shared as a link. Kept by the Logs page so its header can show them. The Sender and Intent
 * options are not read here: the Filters popover asks for them only while it is open.
 */
export function useDeviationLogFilters() {
	const [searchParams, setSearchParams] = useSearchParams();
	const state = useMemo(() => readDeviationParams(searchParams), [searchParams]);
	const update = useCallback(
		(patch: DeviationUrlPatch) => setSearchParams((current) => writeDeviationParams(current, patch), { replace: true }),
		[setSearchParams]
	);

	// The search box is typed into here and reaches the URL once typing pauses, so each key does not refetch.
	const [query, setQuery] = useState(state.q ?? '');
	// Follow the URL during render (back/forward, an opened link), not in an effect. The box keeps what
	// is typed when the URL only caught up with it, so a pause in typing never rewrites the box.
	const [shownTicket, setShownTicket] = useState(state.q);
	if (shownTicket !== state.q) {
		setShownTicket(state.q);
		if ((state.q ?? '') !== query.trim()) setQuery(state.q ?? '');
	}
	useEffect(() => {
		const timer = setTimeout(() => {
			if (query.trim() !== (state.q ?? '')) update({ q: query });
		}, SEARCH_PAUSE_MS);
		return () => clearTimeout(timer);
	}, [query]); // eslint-disable-line react-hooks/exhaustive-deps -- once per pause in typing

	return {
		query,
		setQuery,
		searchedTicket: state.q,
		days: state.days,
		setDays: (days: DayRange) => update({ days }),
		/** Unset = both. */
		sentVia: state.sentVia,
		setSentVia: (sentVia: DeviationSentVia | undefined) => update({ sentVia }),
		/** Unset = both. */
		reviewed: state.reviewed,
		setReviewed: (reviewed: DeviationReviewedFilter | undefined) => update({ reviewed }),
		/** Empty = every intent. */
		intents: state.intents,
		setIntents: (intents: string[]) => update({ intents }),
		/** Empty = everyone. */
		senders: state.senders,
		setSenders: (senders: string[]) => update({ senders }),
		editRateMin: state.editRateMin,
		editRateMax: state.editRateMax,
		setEditRateRange: (range: { min?: number; max?: number }) => update({ editRateMin: range.min, editRateMax: range.max }),
		sort: state.sort,
		setSort: (sort: DeviationSort) => update({ sort })
	};
}

export type DeviationLogFilters = ReturnType<typeof useDeviationLogFilters>;

/** The filter the log asks the API for. */
export function toDeviationFilter(
	filters: Pick<
		DeviationLogFilters,
		'searchedTicket' | 'days' | 'sentVia' | 'reviewed' | 'intents' | 'senders' | 'editRateMin' | 'editRateMax' | 'sort'
	>
): DeviationFilter {
	return {
		q: filters.searchedTicket || undefined,
		sentVia: filters.sentVia,
		reviewed: filters.reviewed === undefined ? undefined : filters.reviewed === 'yes' ? 'true' : 'false',
		intent: filters.intents.join(',') || undefined,
		sender: filters.senders.join(',') || undefined,
		editRateMin: filters.editRateMin,
		editRateMax: filters.editRateMax,
		sort: filters.sort === 'recent' ? undefined : filters.sort,
		...sentBetween(filters.days)
	};
}
